// The app's MUI theme, and since U9 it *is* the design language.
//
// **The legacy mirror is gone.** U1 shipped this file as a verbatim copy of
// `ui/theme.ts` under a zero-visible-change contract, because screens still
// painted themselves over `var(--ui-…)` from `palette.css` and a theme that had
// already moved would have shown through every one of them. That contract
// expired at U9: every screen is on the kit, `ui/theme.ts`, `palette.css` and
// `type.css` are deleted, and everything below is built from `TOKENS`.
//
// **The palette here is MUI's copy of the tokens, not a second palette.** MUI
// *computes* on it — alpha, contrast text, disabled shades — which is why it
// has to be handed real colours rather than a `theme.kit` lookup at use time.
// A component of ours reads `theme.kit`; this exists for the MUI internals
// underneath one.
//
// **No `CssBaseline`.** `index.html` already sets the page background, height
// and overflow for a full-window canvas, and baseline would reset those to the
// theme's own. The one thing worth taking from it — dark native controls — is
// `color-scheme: dark` in that same file, which does it for scrollbars and
// `<select>` dropdowns as well. The body's face and colour are `KitGlobals`.

import { createTheme } from "@mui/material";

import { TOKENS } from "./tokens";

// The custom slot, typed where it is filled. `theme.kit` is the design
// language reaching the components that ask MUI for it; `kit/tokens.ts` stays
// the source for everything that cannot see a theme (the render layer).
declare module "@mui/material/styles" {
  interface Theme {
    kit: typeof TOKENS;
  }
  interface ThemeOptions {
    kit?: typeof TOKENS;
  }
}

export const THEME = createTheme({
  kit: TOKENS,
  palette: {
    mode: "dark",
    primary: { main: TOKENS.color.brand },
    secondary: { main: TOKENS.color.cream },
    error: { main: TOKENS.color.rust },
    // **There is no green in this language** (PLAN-UI §28.2), so `success` is
    // cream and `warning` is the brand orange. MUI's own components are the
    // only readers; naming a colour the sheet does not have, to satisfy a
    // slot nothing draws, would be a sixth voice on the page.
    warning: { main: TOKENS.color.brand },
    success: { main: TOKENS.color.cream },
    background: { default: TOKENS.color.ground, paper: TOKENS.color.panel },
    text: {
      primary: TOKENS.color.ink,
      secondary: TOKENS.color.inkDim,
      disabled: TOKENS.color.inkDim
    },
    divider: TOKENS.color.line
  },
  typography: {
    fontFamily: TOKENS.font.body,
    h1: { fontFamily: TOKENS.font.heading },
    h2: { fontFamily: TOKENS.font.heading },
    h3: { fontFamily: TOKENS.font.heading },
    h4: { fontFamily: TOKENS.font.heading },
    h5: { fontFamily: TOKENS.font.heading },
    h6: { fontFamily: TOKENS.font.heading },
    // A button is a label, not a shout. The kit's own `Btn` sets its case
    // itself; this is for the MUI buttons the burn-down list still has.
    button: { textTransform: "none" }
  },
  /*
    **One ladder, and `TOKENS.z` is it** (PLAN-UI §28.1, the U2 debt).

    MUI's defaults — appBar 1100, drawer 1200, modal 1300, snackbar 1400,
    tooltip 1500 — are numbers in the kit's own range in a different order, so
    the two ladders collided silently: `TOKENS.z.popover` is 1200 and a legacy
    `Dialog` came out on top of it. Setting MUI's keys from the tokens makes
    the kit's order the one that holds for MUI's components too.

    MUI requires `appBar <= drawer < modal`. Both are `chrome` here — one rung
    in this language rather than two — which satisfies it by equality. The
    three keys MUI has and this app renders nothing for (`mobileStepper`,
    `fab`, `speedDial`) keep their defaults: they sit below the lowest rung, so
    if one ever ships it appears under everything and is noticed, rather than
    quietly over the board.
  */
  zIndex: {
    appBar: TOKENS.z.chrome,
    drawer: TOKENS.z.chrome,
    modal: TOKENS.z.modal,
    snackbar: TOKENS.z.banner,
    tooltip: TOKENS.z.tooltip
  },
  components: {
    // **This override dies with the last `Tooltip`.** Everything hovered
    // explains itself on a `HoverCard` (U6 ruling); the last user, `MarchGizmo`,
    // is retired (§31 R1), so nothing reads this block now.
    MuiTooltip: {
      defaultProps: { arrow: true, enterDelay: 300, placement: "top" },
      styleOverrides: {
        tooltip: {
          backgroundColor: TOKENS.color.panel,
          color: TOKENS.color.ink,
          border: `1px solid ${TOKENS.color.line}`,
          fontFamily: TOKENS.font.body,
          fontSize: 12,
          fontWeight: 400,
          lineHeight: 1.5,
          // These carry a sentence of rule, not a two-word label. MUI's
          // default caps at 300px, which breaks a real explanation into a
          // narrow column eight lines tall.
          maxWidth: 340,
          padding: "8px 10px"
        },
        arrow: {
          color: TOKENS.color.panel,
          "&::before": { border: `1px solid ${TOKENS.color.line}` }
        }
      }
    },
    // Never a raised sheet of paper: every surface in this app is a panel with
    // a one-pixel edge, and a shadow under one reads as a modal that is not.
    MuiPaper: { defaultProps: { elevation: 0 } }
  }
});
